import React, { Children, useMemo, useRef, useCallback, useLayoutEffect, useState } from 'react'
import {
  LayoutBand,
  Grid,
  List,
  ListItem,
  Divider,
  useContainerWidth,
  Separator,
  Cell,
  Skeleton,
  DataPreview,
  H2,
} from '@fs/zion-ui'
import { BorderScroller } from '@fs/zion-ui/quarks-authorized-use-only'
import ErrorBoundary from '@fs/zion-error-boundary'
import { useIsMdWidth } from '../../../hooks'
import VideoProvider from '../VideoProvider'
import ErrorFallback from '../../ErrorFallback'
import { getDuration } from '../../helpers'

const LoadingCalendar = () => {
  return (
    <div>
      <Skeleton.H1 />
      {Children.toArray(
        [...Array(5)].map(() => (
          <>
            <Skeleton.Image height={65} width="100%" />
            <Separator />
          </>
        ))
      )}
    </div>
  )
}

/**
 * Plays a list of sessions.
 */
export default function ListPlayer({
  sessions,
  loading,
  autoplay,
  heading,
  noSessionText,
  onEnd,
  playingIndex = 0,
  listItems,
  ...props
}) {
  const isMdWidth = useIsMdWidth()
  const atWidth = useContainerWidth()
  const videoContainerRef = useRef()
  const headerContainerRef = useRef()
  const [listHeight, setListHeight] = useState(0)

  const updateListHeight = useCallback(() => {
    const videoHeight = videoContainerRef.current?.clientHeight ?? 0
    const headerHeight = headerContainerRef.current?.clientHeight ?? 0
    setListHeight(Math.max(videoHeight - headerHeight, 0))
  }, [])

  useLayoutEffect(() => {
    updateListHeight()

    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(() => {
        updateListHeight()
      })

      if (videoContainerRef.current) {
        observer.observe(videoContainerRef.current)
      }

      if (headerContainerRef.current) {
        observer.observe(headerContainerRef.current)
      }

      return () => {
        observer.disconnect()
      }
    }

    window.addEventListener('resize', updateListHeight)
    return () => {
      window.removeEventListener('resize', updateListHeight)
    }
  }, [updateListHeight])

  const session = sessions[playingIndex]

  // this is an optimization and a feature; it makes it so that if two videos with the same ids are back to back, the player will not reload
  const video = session?.item?.video?.videoProvider
  const videoProvider = useMemo(() => video ?? {}, [video])
  const videoDuration = getDuration(session?.item)

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <LayoutBand as="section">
        <Grid columns={isMdWidth ? 1 : 2}>
          <div ref={videoContainerRef} columns={atWidth({ lg: 9 })}>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <VideoProvider
                autoplay={autoplay}
                videoProvider={videoProvider}
                onEnd={onEnd}
                loading={loading}
                sessionId={session?.item?.contentId}
                videoStartTime={session?.date}
                videoDuration={videoDuration}
                contentLocale={session?.item?.video?.contentLocale}
                title={session?.item?.title}
                {...props}
              />
            </ErrorBoundary>
          </div>
          <Cell columns={atWidth({ lg: 3 })}>
            <div ref={headerContainerRef}>
              <H2 size="H5" data-testid="main-stage-heading">
                {heading}
              </H2>
            </div>
            <DataPreview.Loading loading={loading}>
              <LoadingCalendar />
            </DataPreview.Loading>
            <DataPreview.Ready loading={loading}>
              <BorderScroller>
                <div style={{ height: listHeight, paddingInlineEnd: 12 }} data-testid="calendar-sessions">
                  <List>
                    {listItems}
                    {sessions.length === 0 && (
                      <>
                        <Divider />
                        <ListItem primaryText={noSessionText} />
                      </>
                    )}
                  </List>
                </div>
              </BorderScroller>
            </DataPreview.Ready>
          </Cell>
        </Grid>
      </LayoutBand>
    </ErrorBoundary>
  )
}
